import { computed, ref } from 'vue';
import type { ComputedRef, Ref, Slots } from 'vue';
import { deepMerge, omit } from '@mts-fe/utils';
import { RegisterFn, ReturnMethods, useModal } from '@mts/components/Modal';
import { t } from '@mts/hooks/web/useI18n';
import type { ActionBtnItem, PageModal } from '..';

export interface PageModalReturnMethods extends ReturnMethods {
  modalsRef: Ref<PageModal[]>;
  setModals: (modals?: PageModal[]) => void;
  getModalById: (modalId: string) => PageModal | void;
  getModalContent: (modal: PageModal) => Omit<PageModal, 'modalId' | 'modal' | 'modalHandler'>;
  showModal: (
    actionEvent: Partial<ActionBtnItem>,
    record?: Recordable,
    modalSchema?: PageModal,
  ) => void;
}
export type UsePageModalReturnType = [RegisterFn, PageModalReturnMethods];

export function usePageModal(name = 'PageModal'): UsePageModalReturnType {
  const modalsRef = ref<PageModal[]>([]);

  const [register, modalAction] = useModal();

  const getModalById = (modalId: string) => {
    // @ts-ignore
    const modalArea = modalsRef.value.find((i) => i.modalId === modalId);
    if (!modalArea) {
      console.error(`【${name}】弹窗${modalId}不存在`, modalsRef.value);
    }
    return modalArea;
  };

  const showModal: PageModalReturnMethods['showModal'] = (
    actionEvent,
    record = {},
    modalSchema,
  ) => {
    const modalArea = modalSchema || getModalById(actionEvent.modalId!);
    if (!modalArea) {
      return;
    }

    const { modalId, modal, modalHandler, ...pageSchema } = modalArea;
    modalAction.setModalProps({
      ...modal,
      title: t(modal.title),
      footer: actionEvent.eventType === 'ROW_DETAIL' ? null : modal.footer,
    });

    let _modalPayload = {};
    if (record._modalPayload) {
      _modalPayload = record._modalPayload;
      delete record._modalPayload;
    }

    modalAction.openModal(true, {
      pageSchema: {
        modals: modalsRef.value,
        ...pageSchema,
        modalPayload: deepMerge(pageSchema.modalPayload || {}, _modalPayload),
      },
      actionEvent: actionEvent.eventKey
        ? { ...actionEvent, eventKey: 'MODAL_' + actionEvent.eventKey }
        : actionEvent,
      modalHandler,
      record,
    });
  };

  return [
    register,
    {
      ...modalAction,
      modalsRef,
      setModals: (modals = []) =>
        modals.length && (modalsRef.value = [...modalsRef.value, ...modals]),
      getModalById,
      getModalContent: (pageModal: PageModal) =>
        omit(pageModal, ['modalId', 'modal', 'modalHandler']),
      showModal,
    },
  ];
}

export function usePageModalSlot(slots: Slots) {
  const getModalSlotKeys: ComputedRef<string[]> = computed(() => {
    const keys = Object.keys(slots);
    return keys
      .map((item) => (item.startsWith('modal-') ? item : null))
      .filter((item) => !!item) as string[];
  });

  function replaceModalSlotKey(key: string) {
    if (!key) return '';
    return key?.replace?.(/modal-/, '') ?? '';
  }

  return { replaceModalSlotKey, getModalSlotKeys };
}
